import Backbone from '@trello/backbone';
import { Socket } from 'app/scripts/network/socket';
import { TrelloStorage } from '@trello/storage';
import { Util } from 'app/scripts/lib/util';
import _ from 'underscore';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';

type Callback = (
  error: string | null | Error,
  result?: object | string,
  socketRPC?: SocketRPC,
) => void;

interface PingMessage {
  type: 'ping';
}

interface SubscribeMessage {
  type: 'subscribe';
  modelType: string;
  idModel: string;
  tags: string[];
  invitationTokens: string[];
}

interface UnsubscribeMessage {
  type: 'unsubscribe';
  modelType: string;
  idModel: string;
}

class SocketRPC {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delegate: any;
  reqid: number;
  dictCallback: {
    [key: `fx${string}`]: Callback;
  };
  dictTime: {
    [key: string]: Date;
  };
  connectedOnce: boolean;
  hasSockets: boolean;
  socket: Socket;
  dateLastMessage: number;
  timeoutInvokeResponse: number | undefined;

  // The `delegate` argument should be changed to type "RPC" once that has been converted to TypeScript.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(delegate: any) {
    let socketEvent;
    this.delegate = delegate;
    this.reqid = 0;
    this.dictCallback = {};
    this.dictTime = {};
    this.connectedOnce = false;

    this.hasSockets =
      'WebSocket' in window && TrelloStorage.get('useWebsockets') !== 'no';

    if (!('WebSocket' in window)) {
      Analytics.sendOperationalEvent({
        actionSubject: 'socket',
        action: 'stopped',
        source: 'network:socketRpc',
      });
    } else if (TrelloStorage.get('useWebsockets') === 'no') {
      Analytics.sendOperationalEvent({
        actionSubject: 'socketPitStop',
        action: 'stopped',
        source: 'network:socketRpc',
      });
    }

    if (!this.hasSockets) {
      return;
    }

    const protocol = document.location.protocol === 'http:' ? 'ws:' : 'wss:';

    this.socket = new Socket(
      `${protocol}//${document.location.host}/1/Session/socket`,
    );

    for (socketEvent of ['connecting', 'reconnecting']) {
      ((event) => {
        // @ts-expect-error
        this.socket.on(event, () => {
          // @ts-expect-error
          this.trigger('connecting');
        });
      })(socketEvent);
    }

    // @ts-expect-error
    this.socket.on('connect_failed', () => {
      // @ts-expect-error
      this.trigger('connect_failed');
    });

    // @ts-expect-error
    this.socket.on('reconnect_failed', () => {
      // @ts-expect-error
      this.trigger('reconnect_failed');

      if (
        this.socket.reconnectNotAttemptedReason === this.socket.previousReason
      ) {
        return;
      }

      const timeSinceGoodConnection = Date.now() - (this.dateLastMessage || 0);

      Analytics.sendOperationalEvent({
        actionSubject: 'socketRedbox',
        action: 'errored',
        source: 'network:socketRpc',
        attributes: {
          timeSinceGoodConnection,
          reconnectNotAttemptedReason: this.socket.reconnectNotAttemptedReason,
        },
      });
    });

    // @ts-expect-error
    this.socket.on('connect', () => {
      // @ts-expect-error
      this.trigger('connect');
      if (this.connectedOnce === false) {
        Analytics.sendOperationalEvent({
          actionSubject: 'socketConnection',
          action: 'connected',
          source: 'network:socketRpc',
          attributes: {
            timeSinceLoadInMilliseconds: Math.round(performance.now()),
          },
        });
      }
      this.connectedOnce = true;
    });

    // @ts-expect-error
    this.socket.on('reconnect', () => {
      window.clearTimeout(this.timeoutInvokeResponse);
      delete this.timeoutInvokeResponse;
      // @ts-expect-error
      this.trigger('reconnect');
    });

    // @ts-expect-error
    this.socket.on('disconnect', (reason) => {
      window.clearTimeout(this.timeoutInvokeResponse);
      delete this.timeoutInvokeResponse;
      _.each(this.dictCallback, (next) =>
        typeof next === 'function' ? next('disconnected') : undefined,
      );
      this.dictCallback = {};
      this.dictTime = {};
    });

    // @ts-expect-error
    this.socket.on('error', () => {
      const isConnected =
        // @ts-expect-error
        this.socket && this.socket.socket && this.socket.socket.connected;

      if (this.connectedOnce && isConnected) {
        // @ts-expect-error
        this.trigger('disconnect');
      }
    });

    // @ts-expect-error
    this.socket.on('data', (msg) => {
      this._messageHandler(msg);
    });
  }

  _messageHandler(msg: {
    reqid: string | null;
    error: string;
    idModelChannel: string;
    ixLastUpdateChannel: string;
    result: object;
    notify: { event: string };
  }) {
    let next;
    this.dateLastMessage = Date.now();

    //For timeout clear, we don't really care if it's the same one, just that the server is alive
    window.clearTimeout(this.timeoutInvokeResponse);
    delete this.timeoutInvokeResponse;

    if (msg.reqid !== null) {
      next = this.dictCallback[`fx${msg.reqid}`];
    }

    if (msg.result) {
      if (typeof next === 'function') {
        next(null, msg.result);
      }
    } else if (msg.notify && msg.notify.event) {
      if (msg.idModelChannel) {
        this.delegate.setIxLastUpdate(
          msg.idModelChannel,
          msg.ixLastUpdateChannel,
        );
      }
      // @ts-expect-error
      this.trigger(
        msg.notify.event,
        _.extend({ idModelChannel: msg.idModelChannel }, msg.notify),
      );
    } else if (msg.error) {
      if (typeof next === 'function') {
        next(msg.error);
      }
    }

    if (msg.reqid !== null) {
      delete this.dictCallback[`fx${msg.reqid}`];
      delete this.dictTime[msg.reqid];
    }
  }

  subscribe(
    modelType: string,
    idModel: string,
    tags: string[],
    next: (err: Error, ixLastUpdate: string | object) => void,
  ) {
    this.send(
      {
        type: 'subscribe',
        modelType,
        idModel,
        tags,
        invitationTokens: Util.invitationTokens(),
      },
      (err, ixLastUpdate) => {
        if (err) {
          Analytics.sendOperationalEvent({
            actionSubject: 'socketSubscribe',
            action: 'errored',
            source: 'network:socketRpc',
            attributes: {
              error: err,
            },
          });
          if (!this.delegate.handledSubscriptionErrors.includes(err)) {
            // Our delegate doesn't know how to handle this error
            // Consider the socket bad and disconnect.
            Analytics.sendOperationalEvent({
              actionSubject: 'socketSubscribe',
              action: 'disconnected',
              source: 'network:socketRpc',
              attributes: {
                error: err,
              },
            });
            this.disconnect();
          }
        } else {
          // If the ixLastUpdate has changed, resync and let that response set ixLastUpdate
          const storedIxLastUpdate = this.delegate.getIxLastUpdate(idModel);
          if (
            storedIxLastUpdate !== undefined &&
            storedIxLastUpdate !== -1 &&
            storedIxLastUpdate !== ixLastUpdate
          ) {
            this.resync(idModel);
          } else {
            this.delegate.setIxLastUpdate(idModel, ixLastUpdate);
          }
        }
        return typeof next === 'function' ? next(err, ixLastUpdate) : undefined;
      },
    );
  }

  unsubscribe(modelType: string, idModel: string, next?: Callback) {
    this.send(
      {
        type: 'unsubscribe',
        modelType,
        idModel,
      },
      (err, response) => {
        return typeof next === 'function' ? next(err, response) : undefined;
      },
    );
  }

  ping(next: Callback) {
    // We need a way to test ping failure in order to verify that polling works in the event that the client supports
    // WebSockets, but fails to establish a connection before the timeout.
    if (featureFlagClient.get('force-websocket-ping-failure', false)) {
      return typeof next === 'function'
        ? next(new Error('Forcing ping failure'), {}, this)
        : undefined;
    } else {
      this.send(
        {
          type: 'ping',
        },
        (err, response) => {
          return typeof next === 'function'
            ? next(err, response, this)
            : undefined;
        },
      );
    }
  }

  send(
    msg: PingMessage | SubscribeMessage | UnsubscribeMessage,
    next: (err: Error, response: string | object) => void,
  ) {
    this.dictCallback[`fx${this.reqid}`] = next;
    this.dictTime[this.reqid] = new Date();

    const sMsg = JSON.stringify({
      ...msg,
      reqid: this.reqid++,
    });

    this.socket.send(sMsg);
    if (!this.timeoutInvokeResponse) {
      this.timeoutInvokeResponse = window.setTimeout(() => {
        if (this.socket.state !== Socket.CONNECTED) {
          // The socket knows it isn't connected.
          return;
        }
        window.clearTimeout(this.timeoutInvokeResponse);
        delete this.timeoutInvokeResponse;
        if (this.socket) {
          this.socket.disconnect();
        }
      }, 10000);
    }
  }

  resync(...idModels: string[]) {
    this.delegate.rpcAJAX.doPoll(
      _.isEmpty(idModels) ? null : idModels,
      this,
      function () {},
    );
  }

  disconnect() {
    this.socket.disconnect();
  }

  destroy() {
    this.socket.destroy();
  }

  hasSocketTransport() {
    return this.hasSockets;
  }
}

Object.assign(SocketRPC.prototype, Backbone.Events);

export { SocketRPC };
